import { BDContainer, BDPageSpinner } from '@brightdrop/bd-ui';
import { Role } from '@gm-commercial/profile-model';
import { Box, Container } from '@mui/material';
import { Suspense, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { AppSpaceUpdater } from '~/app/appContent/AppSpaceUpdater';
import RouteErrorBoundary from '~/app/errorFallback/RouteErrorBoundary';
import GmfAdvisorBoundary from '~/common/components/GmfAdvisorBoundary';
import { SecureRoutes } from '~/common/configs/route.config';
import { OrganizationDetailsProvider } from '~/common/contexts/OrganizationDetailsContext';
import { UserProvider } from '~/common/contexts/UserContext';
import useCurrentContext from '~/common/hooks/useCurrentContext';
import useHeapEventContext from '~/common/hooks/useHeapEventContext';
import useNativeNavigation from '~/common/hooks/useNativeNavigation';
import { AppNames } from '~/common/models/appNames.model';
import { AppRoutePaths } from '~/common/models/route.model';
import { trackCspAppEntryOncePerSession } from '~/common/utils/analytics/heap.utils';
import { lazyRemote } from '~/common/utils/lazyRemote';
import {
  findMatchingRouteFromPathname,
  getNavDisabledPaths,
} from '~/common/utils/route/route.utils';
import { GlobalSnackbarStack } from '~/components/GlobalSnackbarStack/GlobalSnackbarStack';
import { PendingWorkflowsSnackbar } from '~/components/PendingWorkflowsSnackbar/PendingWorkflowsSnackbar';
import SidekickDockMount from '~/components/SidekickDockMount';
import { ActivationProgressSnackbar } from '~/features/catalog/shared/components/activationProgress';
import { getAppNameFromPath } from '~/features/navigationDrawer/config/navDrawer.config';
import InactiveUserCountdownModalWrapper from '~/hooks/useInactivityTracker/InactiveUserCountdownModalWrapper';
import Auth from '~/routes/auth/Auth';
import InboundRedirect from '~/routes/auth/login/InboundRedirect';
import usePublicRoutes from '~/routes/PublicRoutes';
import SecureRoute from '~/routes/secureRoute/SecureRoute';
import { selectRole } from '~/store/profile/profileSlice.selectors';

const BusinessEnrollmentApp = lazyRemote(
  () => import('mf-business-enrollment/BusinessEnrollmentApp')
);
const CsBackofficeApp = lazyRemote(
  () => import('mf-cs-backoffice/CsBackofficeApp')
);

const AppContent = (): JSX.Element => {
  const role = useSelector(selectRole);
  const standardContent = !!role && role !== Role.GMF_ADVISOR;
  const { pathname } = useLocation();

  const appName = getAppNameFromPath(pathname);
  const selectedContext = useCurrentContext();
  const onAuthPage =
    appName === AppNames.AUTH || pathname.startsWith(AppRoutePaths.LOGIN);
  // Certain content-only or flow-specific views should not display global nav components
  const disableNavDrawer = getNavDisabledPaths().some((path) =>
    matchPath(path, pathname)
  );
  const className = [!disableNavDrawer && 'contentWrapper']
    .filter(Boolean)
    .join(' ');

  useNativeNavigation();
  useHeapEventContext();
  const publicRoutes = usePublicRoutes();

  useEffect(() => {
    if (onAuthPage || !selectedContext?.organizationsId) return;

    trackCspAppEntryOncePerSession({
      organizationId: selectedContext?.organizationsId,
      fleetId: selectedContext?.fleetsId,
      path: pathname,
    });
  }, [onAuthPage, selectedContext, pathname]);

  /**
   * Routes can register a `customTemplate` to override or extend
   * the default layout and styling applied globally via `BDContainer.styles.ts`.
   *
   * This allows a route to opt out of specific layout constraints, spacing rules,
   * or container styles — such as `.contentWrapper`.
   *
   * Use custom templates for pages that need full-bleed designs, custom padding, or unique layouts.
   * The component still inherits default styles, but the template can selectively override or extend them.
   *
   * Usage:
   * - Add a `customTemplate` to a route in `route.config.ts`.
   * - It must be a React component that accepts `{ children }`.
   */

  const CustomTemplate = useMemo(() => {
    const route = findMatchingRouteFromPathname(pathname);
    return route?.customTemplate;
  }, [pathname]);

  const appContent = (
    <Suspense fallback={<BDPageSpinner />}>
      <RouteErrorBoundary>
        <Routes>
          <Route index element={<InboundRedirect />} />
          {Auth}
          {SecureRoutes.map((route) => {
            const Element = route.element;
            return (
              <Route element={<SecureRoute {...route} />} key={route.name}>
                <Route
                  key={route.name}
                  path={route.path}
                  // The organization provider has to sit inside the route that
                  // carries the path so it can read `:organizationsId` from
                  // `useParams`; anything above `Routes` sees empty params.
                  element={
                    <OrganizationDetailsProvider>
                      <Element />
                    </OrganizationDetailsProvider>
                  }
                />
              </Route>
            );
          })}
          {publicRoutes}
          <Route
            path={`${AppRoutePaths.BUSINESS_ENROLLMENT}/*`}
            element={<BusinessEnrollmentApp />}
          />
          <Route
            path={`${AppRoutePaths.CS_BACKOFFICE}/*`}
            element={<CsBackofficeApp />}
          />
          <Route
            path="*"
            element={<Navigate to={AppRoutePaths.PAGE_NOT_FOUND} />}
          />
        </Routes>
        {/* Global bottom-right widgets share one stack so they never overlap.
        The vehicle-import progress (pending workflows) and the device-activation
        progress are persisted on all authenticated pages for a user. */}
        {standardContent && (
          <GlobalSnackbarStack>
            <PendingWorkflowsSnackbar />
            <ActivationProgressSnackbar />
          </GlobalSnackbarStack>
        )}
      </RouteErrorBoundary>
    </Suspense>
  );

  return (
    <BDContainer id="fleet-management">
      {/* The user provider needs no route params, so it sits above the routes
      rather than on each one: the responses stay subscribed across navigation
      and every view reads the same user. */}
      <UserProvider>
        <Suspense fallback={<BDPageSpinner />}>
          <RouteErrorBoundary>
            <InactiveUserCountdownModalWrapper />
          </RouteErrorBoundary>
        </Suspense>
        {standardContent && <AppSpaceUpdater />}
        <GmfAdvisorBoundary>
          <Container maxWidth={false} className="contentContainer">
            {CustomTemplate ? (
              <CustomTemplate>{appContent}</CustomTemplate>
            ) : (
              <Box
                flex="1"
                className={className}
                position="relative"
                sx={{ width: '100%' }}
              >
                {appContent}
              </Box>
            )}
          </Container>
          {/* Above the route content rather than inside it: a route that
        registers a `customTemplate` reparents that subtree, which would
        remount the assistant mid-conversation and abort the response in
        flight. The mount decides for itself which routes carry the app
        chrome it anchors to. */}
          {standardContent && <SidekickDockMount />}
        </GmfAdvisorBoundary>
      </UserProvider>
    </BDContainer>
  );
};
export default AppContent;
